/**
 * wayai knowledge — an organization's knowledge base (#5142). This reads the invocation; the work is
 * `knowledge/`'s.
 *
 *   refresh-preview  make the knowledge base's preview hold its production base's files
 *                    (`knowledge/refresh-preview.ts`)
 *
 * Usage:
 *   wayai knowledge refresh-preview --org <org_id> --base <knowledge base> [-y]
 */

import { parseArgs } from 'node:util';
import { expected } from '../lib/expected.js';
import { UUID_RE, confirm, wantsHelp } from '../lib/utils.js';
import { createDataClient } from '../data/client.js';
import { isInteractive } from '../data/helpers.js';
import { knowledgeBase } from '../knowledge/remote.js';
import { refreshPreviewKnowledge, type RefreshOptions } from '../knowledge/refresh-preview.js';

const USAGE = `
wayai knowledge — the organization's knowledge base

Usage:
  wayai knowledge refresh-preview --org <org_id> --base <knowledge base> [-y]

refresh-preview: make the knowledge base's preview hold its production base's files — after
  creating the preview, and whenever it should be current again. Files the preview changed are
  replaced and files only it holds are removed; nothing else on it changes, and production is
  only read. It prints what each page copied, removed and left alone; a re-run of a current
  preview changes nothing.

  --base       The production knowledge base, or its preview.
  -y, --yes    Skip the question before copying.
`;

function usageError(message: string): Error {
  return expected(`${message}\n${USAGE}`);
}

const OPTIONS = {
  org: { type: 'string' },
  base: { type: 'string' },
  yes: { type: 'boolean', short: 'y' },
} as const;

function readArgs(args: string[]) {
  try {
    return parseArgs({ args, options: OPTIONS, allowPositionals: true, strict: true });
  } catch (err) {
    throw usageError(err instanceof Error ? err.message : String(err));
  }
}

export type KnowledgeInvocation = { verb: 'refresh-preview' } & RefreshOptions;

/** The subcommands knowledge resources' retirement removed (#5142 step 5): named, ahead of their flags. */
const RETIRED_VERBS = new Set(['migrate', 'cut-over']);

/** The invocation, refused before any request when it is not one this command can run. */
export function parseKnowledgeArgs(args: string[]): KnowledgeInvocation {
  // Read loosely first, so a retired verb is named rather than the first of its flags refused.
  const [retired] = parseArgs({ args, options: OPTIONS, allowPositionals: true, strict: false }).positionals;
  if (retired !== undefined && RETIRED_VERBS.has(retired)) {
    throw expected(
      `\`wayai knowledge ${retired}\` is retired: knowledge resources are gone, and an organization's documents live in its ` +
        'knowledge base as files (`wayai files pull` and `push`).',
    );
  }
  const { values, positionals } = readArgs(args);
  const [verb, ...extra] = positionals;
  if (verb !== 'refresh-preview') {
    throw usageError(verb === undefined ? 'No subcommand given.' : `Unknown subcommand ${JSON.stringify(verb)}.`);
  }
  if (extra.length > 0) throw usageError(`Unexpected argument ${JSON.stringify(extra[0])}.`);
  if (!values.org) throw usageError('--org <org_id> is required.');
  if (!UUID_RE.test(values.org)) throw usageError(`Invalid --org ${JSON.stringify(values.org)}: expected an organization id (a UUID).`);
  if (!values.base) throw usageError('--base <knowledge base> is required.');
  return { verb, orgId: values.org, base: values.base, yes: values.yes === true };
}

export async function knowledgeCommand(args: string[]): Promise<void> {
  if (wantsHelp(args) || args.length === 0) {
    console.log(USAGE);
    return;
  }
  const options = parseKnowledgeArgs(args);
  // Refused before any request: off a terminal no one can answer the question before the writes.
  if (!options.yes && !isInteractive()) throw usageError('It asks before it copies; pass -y when not running in a terminal.');
  // A refresh pages for as long as the knowledge base is large: the client renews a browser login on a 401.
  const data = await createDataClient(options.orgId, { refreshLogin: true });
  await refreshPreviewKnowledge(knowledgeBase(data), options, { log: (line) => console.log(line), confirm });
}
